<template>
  <div class="pb-6">
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
    </div>
    <div v-else-if="error" class="section-error">
      <i class="fas fa-circle-exclamation section-error__icon"></i>
      <p class="section-error__text">{{ error }}</p>
      <button class="btn btn-outline-danger btn-sm" @click="$emit('retry-fetch')">
        <i class="fas fa-rotate-right me-1"></i>Tentar novamente
      </button>
    </div>
    <div v-else-if="alerts.length === 0" class="empty-state text-center py-5">
      <i class="fas fa-circle-check fa-2x text-success mb-3 d-block"></i>
      <p class="text-muted">Nenhum aviso no momento.</p>
    </div>
    <div v-else class="d-flex flex-column gap-2">
      <div
        v-for="alert in alerts"
        :key="alert.withdrawal_request_id"
        class="aviso-item"
      >
        <div class="aviso-item__icon-wrap">
          <i class="fas fa-triangle-exclamation"></i>
        </div>
        <div class="aviso-item__content">
          <div class="aviso-item__top-row">
            <span class="aviso-item__title">{{ alertTitle(alert) }}</span>
            <span class="aviso-item__net">{{ formatCurrency(alert.net_amount) }}</span>
          </div>
          <div class="aviso-item__meta">
            {{ alert.event_name }} · {{ formatDate(alert.failed_at) }} · Meio: {{ paymentMethodLabel(alert) }}
            · Bruto: {{ formatCurrency(alert.amount) }} · Taxa: {{ formatCurrency(alert.transfer_fee) }}
          </div>
          <div class="aviso-item__reason">
            <i class="fas fa-circle-info me-1" style="color:#2563eb;"></i>{{ alertReason(alert) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { formatBRL as formatCurrency } from '@/utils/currencyFormatter';
import { formatDate } from '@/utils/dateFormatter';

const props = defineProps({
  alerts: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
});

defineEmits(['retry-fetch']);

function paymentMethod(alert) {
  const provider = String(alert?.gateway_provider ?? alert?.provider ?? alert?.method ?? '').toLowerCase();
  if (provider === 'asaas' || provider === 'pix') return 'pix';
  if (provider === 'stripe' || provider === 'card' || provider === 'credit_card') return 'card';
  return 'unknown';
}

function paymentMethodLabel(alert) {
  const method = paymentMethod(alert);
  if (method === 'pix') return 'Pix';
  if (method === 'card') return 'cartão';
  return 'não identificado';
}

function alertTitle(alert) {
  const method = paymentMethod(alert);
  if (method === 'pix') return 'Transferência não concluída';
  if (method === 'card') return 'Repasse não concluído';
  return 'Movimentação não concluída';
}

function alertReason(alert) {
  const method = paymentMethod(alert);
  if (method === 'card') {
    return 'Não foi possível concluir este repasse. Nossa equipe está ciente e trabalhando para resolver.';
  }
  if (method === 'pix') {
    return 'Não foi possível completar esta transferência. Nossa equipe está ciente e trabalhando para resolver.';
  }
  return 'Não foi possível concluir esta movimentação. Nossa equipe está ciente e trabalhando para resolver.';
}
</script>

<style scoped>
/* ── Item compacto ── */
.aviso-item {
  background: #fff;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 0.6rem 0.85rem;
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
}
.aviso-item__icon-wrap {
  width: 24px; height: 24px; flex-shrink: 0;
  border-radius: 50%;
  background: #fff0f0;
  border: 1px solid #fecaca;
  display: flex; align-items: center; justify-content: center;
  color: #dc3545; font-size: 0.7rem;
  margin-top: 2px;
}
.aviso-item__content { flex: 1; min-width: 0; }
.aviso-item__top-row {
  display: flex; align-items: baseline;
  justify-content: space-between; gap: 0.5rem;
  margin-bottom: 1px;
}
.aviso-item__title { font-size: 0.78rem; font-weight: 700; color: #dc3545; }
.aviso-item__net { font-size: 0.9rem; font-weight: 700; color: #111827; white-space: nowrap; }
.aviso-item__meta { font-size: 0.7rem; color: #9ca3af; line-height: 1.4; margin-bottom: 3px; }
.aviso-item__reason { font-size: 0.73rem; color: #6b7280; line-height: 1.4; }

/* ── Estados gerais ── */
.empty-state i { opacity: 0.4; }
.pb-6 { padding-bottom: 3rem; }
.section-error {
  text-align: center; padding: 2rem 1.5rem; background: #fff;
  border-radius: 16px; border: 1px solid #fecaca;
}
.section-error__icon { font-size: 1.5rem; color: #ef4444; margin-bottom: 0.5rem; }
.section-error__text { font-size: 0.85rem; color: #6b7280; margin-bottom: 0.75rem; }
</style>
